import { useState } from "react";
import { METALUI_BRAND } from "@workspace/brand";
import { useBranding, resolveLogoSrc } from "@/components/branding-provider";
import { forceClerkRecovery } from "@/lib/clerk-recovery";

const basePath = import.meta.env.BASE_URL.replace(/\/$/, "");

/** sessionStorage key tracking how many times Reset has been clicked this session. */
const RECOVERY_COUNT_KEY = "metalui:clerk-recovery-count";

function getRecoveryCount(): number {
  try {
    return parseInt(window.sessionStorage.getItem(RECOVERY_COUNT_KEY) ?? "0", 10) || 0;
  } catch {
    return 0;
  }
}

function incrementRecoveryCount(): number {
  const next = getRecoveryCount() + 1;
  try {
    window.sessionStorage.setItem(RECOVERY_COUNT_KEY, String(next));
  } catch {
    /* best-effort */
  }
  return next;
}

/**
 * Shown when clerk-js could not finish loading even after one automatic
 * wipe-and-reload (see `lib/clerk-recovery.ts`). This is the manual fallback for
 * the stale-cookie lockout: it explains, in plain language, that the browser is
 * holding on to a saved sign-in from a previous version of the platform, and
 * offers a one-click reset that clears the stale Clerk state and reloads.
 *
 * The auto-recovery already runs first and silently fixes the common case; this
 * screen only appears if a residual (e.g. httpOnly FAPI) cookie survives, and
 * gives the operator an explicit action instead of an endless "Loading…".
 *
 * If the user has already clicked Reset once this session (count ≥ 1), an
 * additional escape-hatch paragraph and link are shown pointing them to a
 * private/incognito window as a definitive workaround.
 */
export default function ClerkStuckRecovery() {
  const branding = useBranding();
  const [resetting, setResetting] = useState(false);
  // Read the current count on first render so the escape-hatch is shown
  // immediately if the user has already tried once.
  const [priorAttempts] = useState(() => getRecoveryCount());
  const logoSrc =
    resolveLogoSrc(branding.logoUrl) ?? `${basePath}/${METALUI_BRAND.shieldFileWhite}`;

  const handleReset = async () => {
    setResetting(true);
    incrementRecoveryCount();
    try {
      await forceClerkRecovery();
    } finally {
      // Re-enable the button in case navigation somehow doesn't fire
      // (e.g. the browser blocked window.location.replace).
      setResetting(false);
    }
  };

  return (
    <div className="flex min-h-[100dvh] items-center justify-center bg-[#0F1115] px-4 py-12">
      <div className="flex w-full max-w-md flex-col items-center gap-6 text-center">
        <div className="flex items-center gap-3">
          <img src={logoSrc} alt={branding.appName} className="h-12 w-12 object-contain" />
          <span className="font-sans text-2xl font-black tracking-tight text-white">
            {branding.appName}
          </span>
        </div>

        <div className="flex flex-col gap-3 rounded-2xl border border-white/10 bg-white/5 p-7">
          <h1 className="text-lg font-semibold text-white">Let&apos;s reset your sign-in</h1>
          <p className="text-sm leading-relaxed text-white/70">
            Your browser is holding on to a saved sign-in from an earlier version of the
            platform, which is stopping this page from loading. Resetting clears that old
            data and takes you to a fresh sign-in — your account and data are safe.
          </p>
          <button
            type="button"
            onClick={handleReset}
            disabled={resetting}
            className="mt-1 inline-flex items-center justify-center rounded-lg bg-[#D4AF37] px-5 py-2.5 text-sm font-semibold text-[#0F1115] transition hover:bg-[#E4C567] disabled:cursor-not-allowed disabled:opacity-60"
          >
            {resetting ? "Resetting…" : "Reset and sign in again"}
          </button>

          {priorAttempts >= 1 && (
            <>
              <p className="mt-2 text-sm leading-relaxed text-white/70">
                Still stuck? Open this page in a private or incognito window, or try a
                different browser — that always clears any remaining saved data.
              </p>
              <button
                type="button"
                onClick={() =>
                  window.open(window.location.origin + basePath + "/sign-in", "_blank")
                }
                className="inline-flex items-center justify-center rounded-lg border border-white/20 px-5 py-2.5 text-sm font-semibold text-white/80 transition hover:border-white/40 hover:text-white"
              >
                Open sign-in in a new tab
              </button>
            </>
          )}

          {priorAttempts === 0 && (
            <p className="mt-2 text-xs leading-relaxed text-white/45">
              Still stuck after resetting? In your browser settings, clear cookies for this
              site (or open a private/incognito window) and try once more.
            </p>
          )}
        </div>
      </div>
    </div>
  );
}
